<script setup lang="ts">
/**
 * The To field of a mail (#1114): an address typed as ever, with the mailbox's contacts offered as it is typed. From
 * the second character, `search` is asked (after a short pause, so each keystroke is not a request) and its matches
 * are listed under the field, each by name and address: the mailbox's own contacts first, then the enterprise's
 * people and members who have an address, as the source answers them. The arrow keys move through the list, Enter or
 * a click puts the address in the field, Escape closes the list. Enter with the list closed submits the form as
 * before. Without `search` it is a plain address field.
 */
import { Input, ZhPanel } from '@zerohuman/ui'
import { computed, onBeforeUnmount, ref, useId } from 'vue'
import type { MailContact } from '@/api'

const props = withDefaults(
    defineProps<{
        modelValue: string
        /** The mailbox's contacts matching what was typed, with the directory. Absent, nothing is offered. */
        search?: (text: string) => Promise<MailContact[]>
        disabled?: boolean
        required?: boolean
        /** The `data-testid` the input itself carries, as the plain field it replaces did. */
        testid?: string
    }>(),
    { search: undefined, disabled: false, required: false, testid: undefined },
)
/** `chosen` follows the update when a suggestion is picked (#1119), so a field of several recipients can add it at once. */
const emit = defineEmits<{ 'update:modelValue': [string]; chosen: [string] }>()

/** How many characters before anything is offered, and how long typing pauses before the search is asked. */
const MIN_CHARACTERS = 2
const PAUSE_MS = 200
/** The most suggestions listed: a field's worth, not a directory. */
const SHOWN_LIMIT = 8

const listId = `mail-recipients-${useId()}`
const suggestions = ref<MailContact[]>([])
const open = ref(false)
const activeIndex = ref(-1)
let timer: ReturnType<typeof setTimeout> | undefined
/** Which search the list shows, so an answer that arrives after a newer one is dropped. */
let asked = 0

const shown = computed(() => suggestions.value.slice(0, SHOWN_LIMIT))
const activeId = computed(() => (open.value && activeIndex.value >= 0 ? `${listId}-${activeIndex.value}` : undefined))

function onInput(value: string | number): void {
    const text = String(value)
    emit('update:modelValue', text)
    if (timer) clearTimeout(timer)
    const term = text.trim()
    if (!props.search || term.length < MIN_CHARACTERS) {
        close()
        return
    }
    timer = setTimeout(() => void lookUp(term), PAUSE_MS)
}

async function lookUp(term: string): Promise<void> {
    const search = props.search
    if (!search) return
    const mine = ++asked
    let found: MailContact[]
    try {
        found = await search(term)
    } catch {
        // Nothing to offer, then: the address can still be typed in full.
        found = []
    }
    if (mine !== asked) return
    suggestions.value = found
    activeIndex.value = -1
    open.value = found.length > 0
}

function close(): void {
    asked++
    open.value = false
    suggestions.value = []
    activeIndex.value = -1
}

function choose(contact: MailContact): void {
    emit('update:modelValue', contact.address)
    emit('chosen', contact.address)
    close()
}

function move(step: number): void {
    const count = shown.value.length
    if (!open.value || count === 0) return
    activeIndex.value = (activeIndex.value + step + count) % count
}

/** Enter picks the highlighted suggestion; with none highlighted it is left to submit the form. */
function onEnter(event: KeyboardEvent): void {
    const pick = open.value ? shown.value[activeIndex.value] : undefined
    if (!pick) return
    event.preventDefault()
    choose(pick)
}

function onEscape(event: KeyboardEvent): void {
    if (!open.value) return
    // Closes the list, not the dialog the field sits in.
    event.stopPropagation()
    close()
}

function onBlur(): void {
    // A suggestion is picked on mousedown, which arrives before this blur. Wait so that click still lands.
    setTimeout(close, 120)
}

onBeforeUnmount(() => {
    if (timer) clearTimeout(timer)
})
</script>

<template>
	<div class="relative">
		<Input
			:model-value="modelValue"
			type="email"
			:required="required"
			:disabled="disabled"
			autocomplete="off"
			role="combobox"
			aria-autocomplete="list"
			:aria-expanded="open"
			:aria-controls="listId"
			:aria-activedescendant="activeId"
			:data-testid="testid"
			@update:model-value="onInput"
			@keydown.down.prevent="move(1)"
			@keydown.up.prevent="move(-1)"
			@keydown.enter="onEnter"
			@keydown.esc="onEscape"
			@blur="onBlur"
		/>
		<ZhPanel
			v-show="open && !disabled"
			:id="listId"
			as="ul"
			flush
			role="listbox"
			aria-label="Contacts"
			class="absolute z-50 mt-1 max-h-64 w-full overflow-auto shadow"
			data-testid="mail-recipient-suggestions"
		>
			<li
				v-for="(contact, index) in shown"
				:id="`${listId}-${index}`"
				:key="contact.address"
				role="option"
				:aria-selected="index === activeIndex"
				:data-contact-address="contact.address"
				:data-contact-source="contact.source"
				class="flex cursor-pointer flex-col px-3 py-1.5 text-left text-sm"
				:class="index === activeIndex ? 'bg-muted' : 'hover:bg-muted/50'"
				@mousedown.prevent="choose(contact)"
			>
				<span class="font-medium">{{ contact.name || contact.address }}</span>
				<span v-if="contact.name" class="text-muted-foreground text-xs">
					{{ contact.address }}<template v-if="contact.source === 'directory'"> · In your enterprise</template>
				</span>
				<span v-else-if="contact.source === 'directory'" class="text-muted-foreground text-xs">In your enterprise</span>
			</li>
		</ZhPanel>
	</div>
</template>
